/**
 * `useSupportConfig` — pulls SLA + workload thresholds from the singleton
 * `SupportConfiguration` model on the backend and exposes them everywhere
 * a page needs to color "late" cells, raise alerts, or annotate a chart.
 *
 * Backend endpoint:
 *   GET /system/support-config
 *   (singleton_model.views.SupportConfigurationView, returns whatever
 *    fields the SupportConfiguration model has — currently nullable ints.)
 *
 * The fetch is cached for 10 minutes — this config almost never changes
 * during a session. We deliberately do NOT plumb this through React Query
 * suspense so that a missing endpoint or partial config never blocks the
 * dashboard; callers fall back to sensible defaults.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { get } from '../client';

export interface SupportConfig {
  /** Cap on how many SKUs a single picker session can grab. */
  sku_count_limit: number | null;
  /** Soft / hard packing budgets in seconds. */
  packing_normal_time: number | null;
  packing_limit_time: number | null;
  /** Soft / hard biller budgets in seconds. */
  biller_normal_time: number | null;
  biller_limit_time: number | null;
  /** How long picking is allowed before we mark it `picking-late` (sec). */
  picking_time: number | null;
  /** How long support has to act on an exception before it escalates (sec). */
  support_action_time: number | null;
  /** Rider stage SLAs in seconds (primary → secondary → tertiary). */
  rider_time: number | null;
  secondary_rider_time: number | null;
  tertiary_rider_time: number | null;
  delivery_handover_seconds: number | null;
  return_entry_seconds: number | null;
  return_racking_seconds: number | null;
}

/**
 * Fallback values used when the endpoint hasn't loaded yet, the call
 * failed, or a specific field is null on the singleton. These mirror the
 * staff app's hard-coded defaults from before the singleton landed.
 */
export const DEFAULT_SUPPORT_CONFIG: SupportConfig = {
  sku_count_limit: 60,
  packing_normal_time: 5 * 60,
  packing_limit_time: 10 * 60,
  biller_normal_time: 3 * 60,
  biller_limit_time: 6 * 60,
  picking_time: 10 * 60,
  support_action_time: 5 * 60,
  rider_time: 30 * 60,
  secondary_rider_time: 45 * 60,
  tertiary_rider_time: 60 * 60,
  delivery_handover_seconds: 5 * 60,
  return_entry_seconds: 10 * 60,
  return_racking_seconds: 15 * 60,
};

const ENDPOINT = '/system/support-config';

export function useSupportConfigQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['legacy', 'support-config'],
    queryFn: async () => {
      const data = await get<Partial<SupportConfig>>(ENDPOINT);
      return mergeWithDefaults(data ?? {});
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    // Avoid retry storms on 401 — some stacks return "credentials not provided"
    // for legacy routes until auth is fully wired; React Query retries would
    // hammer the server and stress Radix-heavy shells.
    retry: false,
  });
}

function mergeWithDefaults(partial: Partial<SupportConfig>): SupportConfig {
  const out: SupportConfig = { ...DEFAULT_SUPPORT_CONFIG };
  (Object.keys(out) as (keyof SupportConfig)[]).forEach((k) => {
    const v = partial[k];
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
  });
  return out;
}

// ---------------------------------------------------------------------------
// Context wrapper — lets non-React-Query callers (e.g. plain functions
// rendered through `useFilters` selectors) read the config synchronously.
// ---------------------------------------------------------------------------

const SupportConfigContext = createContext<SupportConfig>(DEFAULT_SUPPORT_CONFIG);

export function SupportConfigProvider({ children }: { children: ReactNode }) {
  const { ready, isAuthenticated } = useAuth();
  const { data } = useSupportConfigQuery(ready && isAuthenticated);
  const value = useMemo(() => data ?? DEFAULT_SUPPORT_CONFIG, [data]);
  return <SupportConfigContext.Provider value={value}>{children}</SupportConfigContext.Provider>;
}

export function useSupportConfig(): SupportConfig {
  return useContext(SupportConfigContext);
}

// ---------------------------------------------------------------------------
// Helpers used by SLA tagging across the app.
// ---------------------------------------------------------------------------

/**
 * Pick a coarse SLA bucket for an order in a given pipeline stage.
 *
 *   'on-time' — under the normal threshold (or no data)
 *   'soft'    — past the normal threshold, under the hard limit
 *   'late'    — past the hard limit
 */
export type SlaBucket = 'on-time' | 'soft' | 'late';

export function bucketize(
  ageSeconds: number,
  normalSeconds: number | null,
  limitSeconds: number | null,
): SlaBucket {
  if (limitSeconds && ageSeconds >= limitSeconds) return 'late';
  if (normalSeconds && ageSeconds >= normalSeconds) return 'soft';
  return 'on-time';
}
